import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  updateDoc,
  runTransaction,
  onSnapshot,
  db
} from '../config/firebase';
import { transformDocument, camelToSnake } from './firestoreService';
import { getBallotsPath } from './firestorePaths';
import userService from './userService';

const FETCH_CACHE_TTL_MS = 60 * 1000;
const ballotsFetchCache = new Map();

/**
 * BallotService
 * Handles operations for ballots subcollection
 */
class BallotService {
  /**
   * Fetch all ballots for a party, ordered by total_score descending (or date_updated)
   * @param {string} oscarsPartyId - The Oscar party document ID
   * @returns {Promise<Array<BallotModel>>} Array of ballot models
   */
  async fetchBallots(oscarsPartyId) {
    const cachedEntry = ballotsFetchCache.get(oscarsPartyId);
    if (cachedEntry && (Date.now() - cachedEntry.timestamp) < FETCH_CACHE_TTL_MS) {
      return cachedEntry.data;
    }

    try {
      const ballotsRef = collection(db, getBallotsPath(oscarsPartyId));
      // Order by total_score descending for leaderboard, fallback to date_updated
      let q;
      try {
        q = query(ballotsRef, orderBy('total_score', 'desc'));
      } catch (indexError) {
        // If index doesn't exist, order by date_updated
        console.warn('Index for total_score not found, ordering by date_updated');
        q = query(ballotsRef, orderBy('date_updated', 'desc'));
      }
      const querySnapshot = await getDocs(q);

      const ballots = [];
      querySnapshot.forEach((docSnapshot) => {
        const ballotData = transformDocument(docSnapshot.id, docSnapshot.data());
        ballots.push(ballotData);
      });

      ballotsFetchCache.set(oscarsPartyId, {
        data: ballots,
        timestamp: Date.now(),
      });

      return ballots;
    } catch (error) {
      console.error('Error fetching ballots:', error);
      throw error;
    }
  }

  /**
   * Fetch a user's ballot by ballot_id (which is now userId)
   * @param {string} oscarsPartyId - The Oscar party document ID
   * @param {string} ballotId - The ballot_id field value (which is userId)
   * @returns {Promise<BallotModel | null>} Ballot model or null if not found
   */
  async fetchUserBallot(oscarsPartyId, ballotId) {
    try {
      // Since ballot_id is now userId and document ID is userId, use fetchBallotById
      return await this.fetchBallotById(oscarsPartyId, ballotId);
    } catch (error) {
      console.error('Error fetching user ballot:', error);
      throw error;
    }
  }

  /**
   * Fetch a user's ballot by userId
   * @param {string} oscarsPartyId - The Oscar party document ID
   * @param {string} userId - The user document ID
   * @returns {Promise<BallotModel | null>} Ballot model or null if not found
   */
  async fetchBallotByUserId(oscarsPartyId, userId) {
    try {
      const ballotsRef = collection(db, getBallotsPath(oscarsPartyId));

      // Try query with orderBy first, fallback to simple query if index doesn't exist
      let querySnapshot;
      try {
        const q = query(ballotsRef, where('user_id', '==', userId), orderBy('date_updated', 'desc'));
        querySnapshot = await getDocs(q);
      } catch (indexError) {
        // If index doesn't exist, query without orderBy and sort in memory
        console.warn('Index for user_id + date_updated not found, querying without orderBy');
        const q = query(ballotsRef, where('user_id', '==', userId));
        querySnapshot = await getDocs(q);
      }

      if (querySnapshot.empty) {
        return null;
      }

      // Collect all ballots
      let ballots = [];
      querySnapshot.forEach((docSnapshot) => {
        const ballotData = transformDocument(docSnapshot.id, docSnapshot.data());
        ballots.push(ballotData);
      });

      // If we didn't use orderBy, sort by dateUpdated descending (most recent first)
      if (ballots.length > 1) {
        ballots.sort((a, b) => {
          const dateA = a.dateUpdated || a.dateCreated || 0;
          const dateB = b.dateUpdated || b.dateCreated || 0;
          return dateB - dateA;
        });
      }

      return ballots[0]; // Return most recent ballot
    } catch (error) {
      console.error('Error fetching ballot by userId:', error);
      throw error;
    }
  }

  /**
   * Fetch a ballot by document ID
   * @param {string} oscarsPartyId - The Oscar party document ID
   * @param {string} documentId - The Firestore document ID
   * @returns {Promise<BallotModel | null>} Ballot model or null if not found
   */
  async fetchBallotById(oscarsPartyId, documentId) {
    try {
      const ballotRef = doc(db, getBallotsPath(oscarsPartyId), documentId);
      const docSnapshot = await getDoc(ballotRef);

      if (!docSnapshot.exists()) {
        return null;
      }

      return transformDocument(docSnapshot.id, docSnapshot.data());
    } catch (error) {
      console.error('Error fetching ballot by ID:', error);
      throw error;
    }
  }

  /**
   * Create a new ballot
   * @param {string} oscarsPartyId - The Oscar party document ID
   * @param {Object} ballotData - Ballot data with camelCase keys (must include userId)
   * @returns {Promise<string>} The document ID of the created ballot (which is userId)
   */
  async createBallot(oscarsPartyId, ballotData) {
    try {
      if (!ballotData.userId) {
        throw new Error('userId is required to create a ballot');
      }

      const userId = ballotData.userId;
      const ballotsRef = collection(db, getBallotsPath(oscarsPartyId));

      // Fetch user to get userDisplayName
      let userDisplayName = ballotData.userDisplayName;
      if (!userDisplayName) {
        try {
          const user = await userService.getUserById(userId);
          userDisplayName = user?.displayName || user?.name || '';
        } catch (userError) {
          console.warn('Could not fetch user for userDisplayName:', userError);
          userDisplayName = '';
        }
      }

      // Remove ballotId from data if present (we'll set it to userId)
      const { ballotId, ...dataWithoutBallotId } = ballotData;

      // Convert camelCase to snake_case for Firestore
      const firestoreData = camelToSnake({
        ...dataWithoutBallotId,
        userDisplayName,
        totalScore: 0,
        correctCount: 0,
        oscarsPartyId,
        dateCreated: Date.now(),
        dateUpdated: Date.now()
      });

      // Use userId as document ID
      const ballotRef = doc(ballotsRef, userId);

      // Use a transaction to atomically create the document
      await runTransaction(db, async (transaction) => {
        // Set the document with ballot_id = userId
        transaction.set(ballotRef, {
          ...firestoreData,
          ballot_id: userId // ballot_id is same as document ID (userId)
        });
      });

      return userId; // Return userId as document ID
    } catch (error) {
      console.error('Error creating ballot:', error);
      throw error;
    }
  }

  /**
   * Update an existing ballot
   * @param {string} oscarsPartyId - The Oscar party document ID
   * @param {string} documentId - The Firestore document ID (userId)
   * @param {Object} updates - Partial update object with camelCase keys
   * @returns {Promise<void>}
   */
  async updateBallot(oscarsPartyId, documentId, updates) {
    try {
      const ballotRef = doc(db, getBallotsPath(oscarsPartyId), documentId);

      // If userDisplayName is not provided in updates, fetch it from user
      let processedUpdates = { ...updates };
      if (!processedUpdates.userDisplayName && documentId) {
        try {
          const user = await userService.getUserById(documentId);
          if (user) {
            processedUpdates.userDisplayName = user.displayName || user.name || '';
          }
        } catch (userError) {
          console.warn('Could not fetch user for userDisplayName update:', userError);
          // Continue without userDisplayName update
        }
      }

      // Convert camelCase to snake_case for Firestore
      const firestoreUpdates = camelToSnake({
        ...processedUpdates,
        dateUpdated: Date.now()
      });

      await updateDoc(ballotRef, firestoreUpdates);
    } catch (error) {
      console.error('Error updating ballot:', error);
      throw error;
    }
  }

  /**
   * Subscribe to ballots updates in real-time
   * @param {string} oscarsPartyId - The Oscar party document ID
   * @param {Function} callback - Function to call when ballots data changes
   * @returns {Function} Unsubscribe function
   */
  subscribeToBallots(oscarsPartyId, callback) {
    try {
      const ballotsRef = collection(db, getBallotsPath(oscarsPartyId));

      // Try query with orderBy first, fallback to simple query if index doesn't exist
      let q;
      try {
        q = query(ballotsRef, orderBy('total_score', 'desc'));
      } catch (indexError) {
        // If index doesn't exist, order by date_updated
        console.warn('Index for total_score not found, ordering by date_updated');
        q = query(ballotsRef, orderBy('date_updated', 'desc'));
      }

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const ballots = [];
          snapshot.forEach((docSnapshot) => {
            const ballotData = transformDocument(docSnapshot.id, docSnapshot.data());
            ballots.push(ballotData);
          });
          callback(ballots);
        },
        (error) => {
          console.error('Error listening to ballots:', error);
          callback([]);
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up ballots subscription:', error);
      callback([]);
      return () => { }; // Return no-op unsubscribe function
    }
  }
}

// Export singleton instance
export default new BallotService();
